import { HeadersFunction, LoaderFunctionArgs, MetaArgs, redirect } from '@remix-run/node';
import { useLoaderData } from '@remix-run/react';
import {
	GQLProductStatusType,
	GQLProductTypeType,
	GetBrandsDocument,
	GetCategoriesDocument,
	GetCategoryDocument,
	GetColourGroupsDocument,
	GetOperationalSettingsDocument,
	GetProductCollectionsDocument,
	GetProductListingPageDocument,
	GetProductTilesWithMinMaxDocument,
	GetSizesDocument,
	GetWebsiteRedirectsDocument,
} from '@theprintbar/graphql';
import { keystoneSudoContext, runSudoGraphql } from '@theprintbar/keystone/context';
import { cachedJsonHour } from '@theprintbar/server';
import { WebRoutes, invariantError, invariantNotFound } from '@theprintbar/shared';
import { ProductListingPage, getProductSearchVariables } from '@theprintbar/ui';
import { SitemapFunction } from 'remix-sitemap';
import { serverOnly$ } from 'vite-env-only/macros';
import { useRootData } from '~/root';
import { contentMeta } from '~/utils/contentMeta';

export const meta = (args: MetaArgs<typeof loader>) => [
	...contentMeta({ data: args.data?.category, matches: args.matches }),
	...((args.data?.initialProducts?.length ?? 0) === 0 ? [{ name: 'robots', content: 'noindex, nofollow' }] : []),
];

export const headers: HeadersFunction = ({ loaderHeaders }) => ({
	'Cache-Control': loaderHeaders.get('Cache-Control') || '',
});

export const sitemap: SitemapFunction = serverOnly$(async ({}) => {
	const categories = await keystoneSudoContext.prisma.category.findMany({
		select: { slug: true, updatedAt: true },
	});

	return (
		categories?.map(({ slug, updatedAt }) => {
			return {
				loc: WebRoutes.Collection(slug),
				lastmod: new Date(updatedAt).toISOString(),
				priority: 0.5,
			};
		}) || []
	);
})!;

export async function loader({ request, params: urlParams }: LoaderFunctionArgs) {
	let slug = urlParams['*'];
	invariantNotFound(slug, 'No product slug provided');
	const { category } = await runSudoGraphql(GetCategoryDocument, { where: { slug } });

	if (!category) {
		const { websiteRedirects } = await runSudoGraphql(GetWebsiteRedirectsDocument, {
			where: { from: { equals: `/collections/${slug}` } },
		});

		if (websiteRedirects?.[0]?.to) {
			return redirect(`${websiteRedirects[0].to}${new URL(request.url).search}`, 301);
		}
		invariantNotFound(category, 'No category found');
	}

	const { params, variables } = getProductSearchVariables({ request, isWeb: true, category });
	const [
		{ productListingPage },
		{ products, maxPrice, minPrice, totalProducts },
		{ operationalSettings },
		{ brands },
		{ categories },
		{ productCollections },
		{ colourGroups },
		{ sizes },
	] = await Promise.all([
		runSudoGraphql(GetProductListingPageDocument),
		runSudoGraphql(GetProductTilesWithMinMaxDocument, variables),
		runSudoGraphql(GetOperationalSettingsDocument),
		runSudoGraphql(GetBrandsDocument, {
			where: {
				products: {
					some: {
						status: { equals: GQLProductStatusType.Published },
						availableWeb: { equals: true }, // Only include brands with products available on the web
					},
				},
			},
		}),
		runSudoGraphql(GetCategoriesDocument),
		runSudoGraphql(GetProductCollectionsDocument, { where: {} }),
		runSudoGraphql(GetColourGroupsDocument),
		runSudoGraphql(GetSizesDocument),
	]);
	const productTypes = Object.values(GQLProductTypeType);

	invariantError(productListingPage, 'No product listing page found');
	invariantError(operationalSettings, 'No operational settings found');

	return cachedJsonHour({
		category,
		productListingPage,
		initialProducts: products,
		minPrice,
		maxPrice,
		totalProducts,
		brands,
		categories,
		collections: productCollections,
		colours: colourGroups,
		sizes,
		operationalSettings,
		params,
		productTypes,
	});
}

export default function () {
	const data = useLoaderData<typeof loader>();
	const { globalContent } = useRootData();
	const samedayCartWarning = globalContent?.samedayCartWarning;

	return (
		<ProductListingPage {...data} productSearchURL={WebRoutes.API.commerce.products.search} samedayCartWarning={samedayCartWarning} />
	);
}
